import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import { setTooltipGlobalOpen, setActionTooltipGlobalOpen, setActionDelayTimeout } from 'src/redux/basic.slice';
import { RootState } from 'src/redux/store';
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 as uuidv4 } from 'uuid';

interface IProps {
    message?: any;
    children: any;
    type?: 'light' | 'dark';
    parentClassName?: string;
    followCursor?: boolean;
    isOpenChildTopPosOnTop?: boolean;
    openOnHover?: boolean;
    childrenClassName?: string;
    tooltipPosition?: 'right' | 'center' | 'left';
    leftAlignOffset?: string;
    actionTooltipCustomClass?: string;
    hoverDelay?: number;
    isMenuClickedKeepsTooltipOpen?: boolean;
    setIsMenuClickedKeepsTooltipOpen?: (value: boolean) => void;
    onClick?: (event: React.MouseEvent) => void;
    rightAlignOffset?: string;
    allowParentClick?: boolean;
    placement?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end';
    onMouseEnter?: () => void;
    /** Called synchronously on hover to resolve placement dynamically.
     *  Avoids parent re-renders during the hover delay — no state needed in the parent. */
    getPlacement?: () => string;
    // Notifies the caller whenever the tooltip's actual shown/hidden state changes — including
    // closes the caller doesn't initiate itself (outside click, Escape, scroll). Optional/additive,
    // mirrors TooltipCmp's existing onOpenChange prop; every existing caller that omits it is
    // unaffected.
    onOpenChange?: (isOpen: boolean) => void;
    // When true, the tooltip arrow is hidden whenever `message` is empty/falsy
    // (e.g. still loading). Default false keeps existing callers unchanged.
    hideArrowWhenNoMessage?: boolean;
}

const ActionTooltip = ({
    message,
    children,
    type = 'dark',
    parentClassName = '',
    childrenClassName = '',
    isOpenChildTopPosOnTop,
    tooltipPosition = 'right',
    openOnHover = false,
    leftAlignOffset,
    actionTooltipCustomClass = '',
    hoverDelay = 100,
    isMenuClickedKeepsTooltipOpen = false,
    setIsMenuClickedKeepsTooltipOpen = () => {},
    onClick,
    rightAlignOffset = 'translateX(-83%)',
    allowParentClick = false,
    placement,
    onMouseEnter,
    getPlacement,
    hideArrowWhenNoMessage = false,
    onOpenChange,
}: IProps) => {
    // useRef so the id is stable across re-renders — a new id on every render
    // changes the trigger's class name, which causes tippy to lose its anchor and blink.
    const uniqueId = useRef(uuidv4()).current;
    const tooltipUniqueId = `tooltipChild-${uniqueId}`;
    const tooltipContentId = `tooltipContent-${uniqueId}`;
    // Treat empty string / null / undefined / false as "no message" (e.g. still loading).
    const hasMessage = message !== undefined && message !== null && message !== false && message !== '';
    const [tooltipOpen, setTooltipOpen] = useState(false);

    type Position = 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end';
    // resolvedPosition is the placement that will be used when the tooltip actually opens.
    // It is updated inside the hover-delay timer (same batch as setTooltipOpen) so the
    // parent never re-renders during the hover sequence, eliminating the blink.
    const [resolvedPosition, setResolvedPosition] = useState<Position>(() => (placement ?? (isOpenChildTopPosOnTop ? 'top' : 'bottom')) as Position);

    const actionTooltipGlobalOpen = useSelector((state: RootState) => state.basic.actionTooltipGlobalOpen);
    const actionDelayTimeout = useSelector((state: RootState) => state.basic.actionDelayTimeout);
    const dispatch = useDispatch();

    // Keep resolvedPosition in sync with static placement/isOpenChildTopPosOnTop props
    // (only relevant when getPlacement is not used).
    useEffect(() => {
        if (!getPlacement) {
            setResolvedPosition((placement ?? (isOpenChildTopPosOnTop ? 'top' : 'bottom')) as Position);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placement, isOpenChildTopPosOnTop]);

    const clearExistingTimeouts = () => {
        if (actionDelayTimeout) {
            clearTimeout(actionDelayTimeout);
            dispatch(setActionDelayTimeout(null));
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (tooltipOpen || isMenuClickedKeepsTooltipOpen) {
                setTooltipOpen(false);
                dispatch(setActionTooltipGlobalOpen(false));
                setIsMenuClickedKeepsTooltipOpen(false);
                clearExistingTimeouts();
            }
        };

        const scrollableContainer = document.querySelector('.main__layout_section');
        scrollableContainer?.addEventListener('scroll', handleScroll);

        // Listen to all types of scrollable containers
        const allScrollableContainers = document.querySelectorAll('.overflow-x-auto, .overflow-y-auto, .overflow-auto, .custom-scrollbar-v2');

        allScrollableContainers.forEach((element) => {
            element.addEventListener('scroll', handleScroll);
        });

        // Also listen to window scroll
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            scrollableContainer?.removeEventListener('scroll', handleScroll);
            allScrollableContainers.forEach((element) => {
                element?.removeEventListener('scroll', handleScroll);
            });
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [tooltipOpen, isMenuClickedKeepsTooltipOpen]);

    useEffect(() => {
        let handleOutsideClick: any;
        let handleEscapePress: any;
        const tooltipChild = document.querySelector(`.${tooltipUniqueId}`);

        if (tooltipOpen && !isMenuClickedKeepsTooltipOpen) {
            handleOutsideClick = (event: any) => {
                if (tooltipChild && !tooltipChild?.contains(event.target)) {
                    setTooltipOpen(false);
                    dispatch(setActionTooltipGlobalOpen(false));
                }
            };

            handleEscapePress = (event: any) => {
                if (tooltipOpen && event.key === 'Escape') {
                    setTooltipOpen(false);
                    dispatch(setActionTooltipGlobalOpen(false));
                }
            };

            document.addEventListener('mousedown', handleOutsideClick);
            document.addEventListener('keydown', handleEscapePress);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscapePress);
        };
    }, [tooltipOpen]);

    useEffect(() => {
        let handleOutsideClick: any;
        let handleEscapePress: any;
        const tooltipChild = document.querySelector(`.${tooltipUniqueId}`);

        if (isMenuClickedKeepsTooltipOpen) {
            handleOutsideClick = (event: any) => {
                const tooltipContent = document.querySelector(`.action-tooltip-custom`);

                if (tooltipChild && !tooltipChild?.contains(event.target) && (!tooltipContent || !tooltipContent?.contains(event.target))) {
                    setTooltipOpen(false);
                    dispatch(setActionTooltipGlobalOpen(false));
                    setIsMenuClickedKeepsTooltipOpen(false);
                }
            };

            handleEscapePress = (event: any) => {
                if (isMenuClickedKeepsTooltipOpen && event.key === 'Escape') {
                    setTooltipOpen(false);
                    dispatch(setActionTooltipGlobalOpen(false));
                    setIsMenuClickedKeepsTooltipOpen(false);
                }
            };

            document.addEventListener('mousedown', handleOutsideClick);
            document.addEventListener('keydown', handleEscapePress);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscapePress);
        };
    }, [isMenuClickedKeepsTooltipOpen]);

    const handleToggleTooltip = (newValue: boolean) => {
        clearExistingTimeouts();

        if (newValue) {
            // Resolve and apply placement BEFORE the delay timer so react-tippy has the
            // correct `position` prop already committed when `open=true` fires 100ms
            // later. Batching them in the same setTimeout callback causes react-tippy to
            // see the old position when it shows the tooltip, producing wrong placement.
            // The parent never holds placement state, so no parent re-renders happen.
            const pos = (getPlacement ? getPlacement() : resolvedPosition) as Position;

            if (pos !== resolvedPosition) {
                setResolvedPosition(pos);
            }
            dispatch(
                setActionDelayTimeout(
                    setTimeout(() => {
                        setTooltipOpen(true);
                        dispatch(setActionTooltipGlobalOpen(true));
                        dispatch(setTooltipGlobalOpen(false));
                    }, hoverDelay)
                )
            );
        } else {
            setTooltipOpen(false);
            dispatch(setActionTooltipGlobalOpen(false));
        }
    };

    useEffect(() => {
        if (!actionTooltipGlobalOpen) {
            setTooltipOpen(false);
        }
    }, [actionTooltipGlobalOpen]);

    const isShown = tooltipOpen || isMenuClickedKeepsTooltipOpen;

    useEffect(() => {
        onOpenChange?.(isShown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShown]);

    const isAtTop = resolvedPosition.startsWith('top');

    return (
        <>
            {/* @ts-ignore */}
            <Tooltip
                html={
                    <div
                        id={tooltipContentId}
                        style={{
                            ...(isAtTop ? { bottom: '0px' } : { top: `0px` }),
                            ...(tooltipPosition === 'right' ? { transform: rightAlignOffset } : {}),
                            ...(tooltipPosition === 'center' ? { transform: 'translateX(-50%)' } : {}),
                            ...(tooltipPosition === 'left' ? { transform: leftAlignOffset } : {}),
                        }}
                        className={`absolute left-1/2 transform break-words cursor-pointer empty:hidden ${parentClassName} ${
                            type === 'light' ? 'tooltipV3' : 'bg-[#0C111B] border border-[#0C111B] text-grayLight px-[6px] py-[6px] '
                        }`}
                    >
                        {message}
                    </div>
                }
                popperOptions={{
                    modifiers: {
                        addCustomClass: {
                            enabled: true,
                            fn: (data: any) => {
                                data.instance.popper.classList.add('action-tooltip-custom');

                                if (actionTooltipCustomClass) {
                                    data.instance.popper.classList.add(...actionTooltipCustomClass.split(' '));
                                }

                                // Hide the arrow when there's no message to show (opt-in via prop)
                                if (hideArrowWhenNoMessage && !hasMessage) {
                                    data.instance.popper.classList.add('action-tooltip-without-arrow');
                                }

                                return data;
                            },
                        },
                    },
                }}
                theme={type}
                duration={0}
                open={isMenuClickedKeepsTooltipOpen || tooltipOpen}
                arrow={true}
                hideOnClick={false}
                trigger="manual"
                position={resolvedPosition}
            >
                <div
                    className={`${childrenClassName} cursor-pointer ${tooltipUniqueId} inline`}
                    onClick={(e) => {
                        if (!allowParentClick) {
                            e.stopPropagation();
                        }

                        if (!openOnHover) {
                            handleToggleTooltip(!tooltipOpen);
                        }

                        if (onClick) {
                            onClick(e);
                        }
                    }}
                    onMouseEnter={() => {
                        if (onMouseEnter) onMouseEnter();
                        if (openOnHover) handleToggleTooltip(true);
                    }}
                    onMouseLeave={() => {
                        if (openOnHover) handleToggleTooltip(false);
                    }}
                >
                    {children}
                </div>
            </Tooltip>
        </>
    );
};

export default ActionTooltip;
